import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box, Button, Typography,
  Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { MainLayout } from '../../components/Layout/MainLayout';
import {
  TaskList, TaskForm, TaskFilterBar, TaskDeleteDialog, TaskKanbanView, TaskDateDialog,
} from '../../components/Task';
import { MiniCalendar } from '../../components/Common/MiniCalendar';
import { useTaskManagement } from '../../hooks/useTaskManagement';
import { useTaskCalendar } from '../../hooks/useTaskCalendar';
import { useTaskStats } from '../../hooks/useTaskStats';
import { TaskData, TaskStatus, CreateTaskRequest, UpdateTaskRequest } from '../../types/task';
import { TaskFilters } from '../../hooks/useTaskManagement';
import { CalendarDot } from '../../types/calendar';

type DialogState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit';   task: TaskData }
  | { type: 'delete'; task: TaskData };


export const TaskListPage: React.FC = () => {
  // ─── カレンダーの年月・選択日ステート ─────────────────────────────────────────
  const { year, month, selectedDate, dialogOpen, handleMonthChange, handleDateClick, handleDialogClose }
    = useTaskCalendar();

  // ─── タスク取得（カレンダーの年月でフィルタ） ──────────────────────────────────
  const {
    tasks, isLoading, error,
    filters, celebrateTaskId,
    createTask, updateTask, deleteTask,
    applyFilters,
  } = useTaskManagement(year, month);

  const { stats } = useTaskStats(tasks);

  // 自分のタスクに登録済みの会社名候補（重複除去・null除外）
  const companyNameOptions = useMemo<string[]>(() => {
    const names = tasks
      .map((t) => t.company_name)
      .filter((name): name is string => !!name);
    return Array.from(new Set(names));
  }, [tasks]);

  // ─── カレンダードット（取得済みタスクの due_date から集計） ────────────────────
  const calendarDots = useMemo<CalendarDot[]>(() => {
    const dateSet = new Set<string>();
    tasks.forEach((t) => { if (t.due_date) dateSet.add(t.due_date); });
    return Array.from(dateSet).map((date) => ({ date, count: 1 }));
  }, [tasks]);

  // ─── 選択日のタスク（ダイアログ用） ────────────────────────────────────────────
  const selectedDateTasks = useMemo<TaskData[]>(() => {
    if (!selectedDate) return [];
    return tasks.filter((t) => t.due_date === selectedDate);
  }, [tasks, selectedDate]);

  // ─── スクロール位置の保持 ──────────────────────────────────────────────────────
  const scrollYRef = useRef(0);

  const handleFilterChange = (newFilters: TaskFilters) => {
    scrollYRef.current = window.scrollY;
    applyFilters(newFilters);
  };

  useEffect(() => {
    if (!isLoading && scrollYRef.current > 0) {
      const saved = scrollYRef.current;
      requestAnimationFrame(() => {
        window.scrollTo({ top: saved, behavior: 'instant' });
      });
    }
  }, [isLoading]);

  // ─── ダイアログ・ビュー状態 ────────────────────────────────────────────────────
  const [dialog, setDialog]               = useState<DialogState>({ type: 'none' });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState<string | null>(null);
  const [viewMode, setViewMode]           = useState<'timeline' | 'kanban'>('timeline');

  const [subTaskCompleteDialog, setSubTaskCompleteDialog] = useState<{
    open: boolean;
    taskId: number;
    updatedSubTasks: any[];
  }>({ open: false, taskId: 0, updatedSubTasks: [] });

  const openCreate  = () => { setActionError(null); setDialog({ type: 'create' }); };
  const openEdit    = (task: TaskData) => { setActionError(null); setDialog({ type: 'edit', task }); };
  const openDelete  = (task: TaskData) => { setActionError(null); setDialog({ type: 'delete', task }); };
  const closeDialog = () => setDialog({ type: 'none' });

  const handleSave = async (data: CreateTaskRequest | UpdateTaskRequest) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (dialog.type === 'create') {
        await createTask(data as CreateTaskRequest);
      } else if (dialog.type === 'edit') {
        await updateTask(dialog.task.id, data as UpdateTaskRequest);
      }
      closeDialog();
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message ?? '保存に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (dialog.type !== 'delete') return;
    setActionLoading(true);
    setActionError(null);
    try {
      await deleteTask(dialog.task.id);
      closeDialog();
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message ?? '削除に失敗しました');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (id: number, status: TaskStatus) => {
    try {
      await updateTask(id, { status });
    } catch {
      // ステータス変更失敗時はサイレント
    }
  };

  const handleSubTaskToggle = async (taskId: number, subTaskId: string, checked: boolean) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || !task.sub_tasks) return;

    const updatedSubTasks = task.sub_tasks.map((s) =>
      s.id === subTaskId ? { ...s, is_completed: checked } : s
    );

    const completedCount = updatedSubTasks.filter((s) => s.is_completed).length;
    const allCompleted   = completedCount === updatedSubTasks.length;

    if (allCompleted) {
      // 親タスクがすでに完了なら確認ダイアログは出さず、チェックリストのみ同期する
      if (task.status === TaskStatus.COMPLETED) {
        try {
          await updateTask(taskId, { sub_tasks: updatedSubTasks });
        } catch { /* サイレント */ }
        return;
      }
      try {
        await updateTask(taskId, { sub_tasks: updatedSubTasks, status: TaskStatus.IN_PROGRESS });
      } catch { /* サイレント */ }
      setSubTaskCompleteDialog({ open: true, taskId, updatedSubTasks });
      return;
    }

    const autoStatus = completedCount === 0 ? TaskStatus.NOT_STARTED : TaskStatus.IN_PROGRESS;
    try {
      await updateTask(taskId, { sub_tasks: updatedSubTasks, status: autoStatus });
    } catch {
      // サイレント
    }
  };

  const handleSubTaskCompleteConfirm = async () => {
    try {
      await updateTask(subTaskCompleteDialog.taskId, { status: TaskStatus.COMPLETED });
    } catch { /* サイレント */ }
    setSubTaskCompleteDialog({ open: false, taskId: 0, updatedSubTasks: [] });
  };

  const handleSubTaskCompleteCancel = () => {
    setSubTaskCompleteDialog({ open: false, taskId: 0, updatedSubTasks: [] });
  };

  return (
    <MainLayout>
      <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>

        {/* ヘッダー */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight={700}>タスク管理</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              選考スケジュールとタスクを管理
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            size="large"
            sx={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            予定を追加
          </Button>
        </Box>

        {/* カレンダー（ビュー切り替えの外側に配置 → 再マウントを防ぐ） */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            bgcolor: 'background.paper',
          }}
        >
          <MiniCalendar
            year={year}
            month={month}
            calendarDots={calendarDots}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
          />
        </Box>

        {/* 統計カード */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: { xs: 1, md: 2 },
            mb: 3,
          }}
        >
          {[
            {
              label: '選考中',
              value: stats.uniqueCompanyCount,
              unit: '社',
              color: '#2e7d32',
            },
            {
              label: '今週の予定',
              value: stats.thisWeekCount,
              unit: '件',
              color: '#f57c00',
            },
            {
              label: '未完了',
              value: stats.incompletedCount,
              unit: '件',
              color: '#1976d2',
            },
          ].map((stat) => (
            <Card
              key={stat.label}
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <CardContent sx={{ px: 2, py: '12px !important' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  {stat.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ color: stat.color, lineHeight: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stat.unit}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* エラー表示 */}
        {(error || actionError) && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {error ?? actionError}
          </Alert>
        )}

        {/* フィルタバー */}
        <TaskFilterBar
          filters={filters}
          onChange={handleFilterChange}
        />

        {/* ビュー切替 */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, val) => { if (val) setViewMode(val); }}
            size="small"
          >
            <ToggleButton value="timeline">
              <ViewListIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              タイムライン
            </ToggleButton>
            <ToggleButton value="kanban">
              <ViewColumnIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
              カンバン
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* タスクビュー
            初回ロード（tasks が空）のみスピナーを表示し、
            フィルタ・ソート・月切り替え時は既存リストを半透明で残す
            → スクロール位置がリセットされない */}
        {isLoading && tasks.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              opacity: isLoading ? 0.5 : 1,
              pointerEvents: isLoading ? 'none' : 'auto',
              transition: 'opacity 0.15s',
            }}
          >
            {viewMode === 'timeline' ? (
              <TaskList
                tasks={tasks}
                celebrateTaskId={celebrateTaskId}
                onEdit={openEdit}
                onDelete={openDelete}
                onStatusChange={handleStatusChange}
                onSubTaskToggle={handleSubTaskToggle}
              />
            ) : (
              <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                <TaskKanbanView
                  tasks={tasks}
                  celebrateTaskId={celebrateTaskId}
                  onEdit={openEdit}
                  onDelete={openDelete}
                  onStatusChange={handleStatusChange}
                  onSubTaskToggle={handleSubTaskToggle}
                  selectedCategory={filters.category}
                />
              </Box>
            )}
          </Box>
        )}

        {/* タスク作成ダイアログ */}
        <TaskForm
          open={dialog.type === 'create'}
          mode="create"
          isLoading={actionLoading}
          companyNameOptions={companyNameOptions}
          onSave={handleSave}
          onClose={closeDialog}
        />

        {/* タスク編集ダイアログ */}
        <TaskForm
          open={dialog.type === 'edit'}
          mode="edit"
          task={dialog.type === 'edit' ? dialog.task : null}
          isLoading={actionLoading}
          companyNameOptions={companyNameOptions}
          onSave={handleSave}
          onClose={closeDialog}
        />

        {/* 削除確認ダイアログ */}
        <TaskDeleteDialog
          open={dialog.type === 'delete'}
          task={dialog.type === 'delete' ? dialog.task : null}
          isLoading={actionLoading}
          onConfirm={handleDelete}
          onCancel={closeDialog}
        />

        {/* サブタスク全完了確認ダイアログ */}
        <Dialog open={subTaskCompleteDialog.open} onClose={handleSubTaskCompleteCancel}>
          <DialogTitle>チェックリストが全て完了しました</DialogTitle>
          <DialogContent>
            <Typography>タスクを「完了」にしますか？</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSubTaskCompleteCancel}>このまま進行中にする</Button>
            <Button onClick={handleSubTaskCompleteConfirm} variant="contained">
              完了にする
            </Button>
          </DialogActions>
        </Dialog>

        {/* 日付クリックダイアログ */}
        <TaskDateDialog
          open={dialogOpen}
          date={selectedDate}
          tasks={selectedDateTasks}
          onClose={handleDialogClose}
          onEdit={openEdit}
          onStatusChange={handleStatusChange}
        />

      </Box>
    </MainLayout>
  );
};

export default TaskListPage;
